import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import Select from '../../components/ui/Select';
import { ArrowLeft, Save } from 'lucide-react';
import { locationService } from '../../services';
import type { Location } from '../../types';
import { nvrHttpUrl } from '../../utils/nvrAccess';

const EditLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState({
    ycl_name: '',
    ycl_code: '',
    ycl_type: '',
    ycl_description: '',
    ycl_address: '',
    ycl_city: '',
    ycl_province: '',
    ycl_postal_code: '',
    ycl_latitude: '',
    ycl_longitude: '',
    ycl_caller_id: '',
    // Cloud Integration
    cloud_provider: '',
    api_key: '',
    app_secret: '',
    cloud_verification_code: '',
    // NVR Configuration
    nvr_ip: '',
    nvr_port: '',
    nvr_internal_port: '',
    nvr_rtsp_port: '',
    nvr_internal_rtsp_port: '',
    nvr_username: '',
    nvr_password_encrypted: '',
    nvr_type: '',
    nvr_model: '',
    nvr_channel_count: '',
    transcoding_enabled: false,
    transcoding_device_ip: '',
    transcoding_device_port: '',
    transcoding_device_api_port: '',
    transcoding_device_username: '',
    transcoding_device_password_encrypted: '',
    transcoding_device_status: 'inactive',
    external_hostname: '',
    external_http_port: '',
    external_transcoding_port: '',
    external_rtsp_port: '',
    external_access_enabled: false,
    stream_auth_enabled: false,
    stream_auth_port: '8000',
    location_identifier: '',
    stream_shared_secret: '',
    ycl_company: '',
    ycl_events_enabled: true,
    ycl_entry_logging: true,
    ycl_defaut_location: false,
    status: true,
    timezone_offset_hours: -8,
    dst_enabled: false,
    location_timezone: 'America/Toronto',
    // Auto-refresh settings for live view grid
    auto_refresh_enabled: true,
    auto_refresh_interval_minutes: 9.5,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchLocation(parseInt(id));
    }
  }, [id]);

  const fetchLocation = async (locationId: number) => {
    try {
      setIsLoading(true);
      const loc = await locationService.getLocationById(locationId);
      setLocation(loc);
      setFormData({
        ycl_name: loc.ycl_name,
        ycl_code: loc.ycl_code,
        ycl_type: loc.ycl_type || '',
        ycl_description: loc.ycl_description || '',
        ycl_address: loc.ycl_address || '',
        ycl_city: loc.ycl_city || '',
        ycl_province: loc.ycl_province || '',
        ycl_postal_code: loc.ycl_postal_code || '',
        ycl_latitude: loc.ycl_latitude?.toString() || '',
        ycl_longitude: loc.ycl_longitude?.toString() || '',
        ycl_caller_id: loc.ycl_caller_id || '',
        // Cloud credentials won't be returned from API for security, but include in state
        cloud_provider: loc.cloud_provider || '',
        api_key: '',
        app_secret: '',
        cloud_verification_code: '',
        // NVR Configuration
        nvr_ip: loc.nvr_ip || '',
        nvr_port: loc.nvr_port?.toString() || '',
        nvr_internal_port: loc.nvr_internal_port?.toString() || '',
        nvr_rtsp_port: loc.nvr_rtsp_port?.toString() || '',
        nvr_internal_rtsp_port: loc.nvr_internal_rtsp_port?.toString() || '',
        nvr_username: loc.nvr_username || '',
        nvr_password_encrypted: loc.nvr_password_encrypted || '',
        nvr_type: loc.nvr_type || '',
        nvr_model: loc.nvr_model || '',
        nvr_channel_count: loc.nvr_channel_count?.toString() || '',
        transcoding_enabled: loc.transcoding_enabled || false,
        transcoding_device_ip: loc.transcoding_device_ip || '',
        transcoding_device_port: loc.transcoding_device_port?.toString() || '',
        transcoding_device_api_port: loc.transcoding_device_api_port?.toString() || '',
        transcoding_device_username: loc.transcoding_device_username || '',
        transcoding_device_password_encrypted: loc.transcoding_device_password_encrypted || '',
        transcoding_device_status: loc.transcoding_device_status || 'inactive',
        external_hostname: loc.external_hostname || '',
        external_http_port: loc.external_http_port?.toString() || '',
        external_transcoding_port: loc.external_transcoding_port?.toString() || '',
        external_rtsp_port: loc.external_rtsp_port?.toString() || '',
        external_access_enabled: loc.external_access_enabled || false,
        stream_auth_enabled: loc.stream_auth_enabled || false,
        stream_auth_port: loc.stream_auth_port?.toString() || '8000',
        location_identifier: loc.location_identifier || '',
        stream_shared_secret: '',
        ycl_company: loc.ycl_company?.toString() || '',
        ycl_events_enabled: loc.ycl_events_enabled !== false,
        ycl_entry_logging: loc.ycl_entry_logging !== false,
        ycl_defaut_location: loc.ycl_defaut_location || false,
        status: loc.status !== false,
        timezone_offset_hours: loc.timezone_offset_hours ?? -8,
        dst_enabled: loc.dst_enabled || false,
        location_timezone: loc.location_timezone || 'America/Toronto',
        // Auto-refresh settings
        auto_refresh_enabled: loc.auto_refresh_enabled !== false,
        auto_refresh_interval_minutes: loc.auto_refresh_interval_minutes ?? 9.5,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch location');
    } finally {
      setIsLoading(false);
    }
  };

  // Show both cloud and NVR sections for all locations
  const showCloudSection = true;  // Always show cloud credentials section
  const showNVRSection = true;    // Always show NVR configuration section
  
  // Determine if this is a cloud-managed location
  const isCloudLocation = !!(location?.cloud_provider || formData.cloud_provider);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.ycl_name.trim()) errors.ycl_name = 'Location name is required';
    if (!formData.ycl_code.trim()) errors.ycl_code = 'Location code is required';
    
    if (formData.external_access_enabled) {
      try { nvrHttpUrl(formData.external_hostname, formData.external_http_port, formData.nvr_port); }
      catch (error) { errors.external_hostname = error instanceof Error ? error.message : 'Check the NVR HTTP/ISAPI address.'; }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location || !validateForm()) return;

    try {
      // Clean and convert data
      const payload: any = {
        ycl_id: location.ycl_id,
        ycl_name: formData.ycl_name.trim(),
        ycl_code: formData.ycl_code.trim(),
      };

      // Add optional string fields (only if not empty)
      if (formData.ycl_type) payload.ycl_type = formData.ycl_type;
      if (formData.ycl_description) payload.ycl_description = formData.ycl_description;

      // Cloud credentials (only if user enters them to update)
      if (formData.api_key) payload.api_key = formData.api_key;
      if (formData.app_secret) payload.app_secret = formData.app_secret;
      if (formData.cloud_verification_code) payload.cloud_verification_code_encrypted = formData.cloud_verification_code;
      if (formData.ycl_address) payload.ycl_address = formData.ycl_address;
      if (formData.ycl_city) payload.ycl_city = formData.ycl_city;
      if (formData.ycl_province) payload.ycl_province = formData.ycl_province;
      if (formData.ycl_postal_code) payload.ycl_postal_code = formData.ycl_postal_code;
      if (formData.ycl_caller_id) payload.ycl_caller_id = formData.ycl_caller_id;

      // Add numeric fields (parse and validate)
      if (formData.ycl_latitude) {
        const lat = parseFloat(formData.ycl_latitude);
        if (!isNaN(lat)) payload.ycl_latitude = lat;
      }
      if (formData.ycl_longitude) {
        const lng = parseFloat(formData.ycl_longitude);
        if (!isNaN(lng)) payload.ycl_longitude = lng;
      }
      if (formData.ycl_company) {
        const company = parseInt(formData.ycl_company);
        if (!isNaN(company)) payload.ycl_company = company;
      }

      // NVR Configuration
      if (formData.nvr_ip) payload.nvr_ip = formData.nvr_ip;
      if (formData.nvr_port) {
        const port = parseInt(formData.nvr_port);
        if (!isNaN(port)) payload.nvr_port = port;
      }
      if (formData.nvr_internal_port) {
        const internalPort = parseInt(formData.nvr_internal_port);
        if (!isNaN(internalPort)) payload.nvr_internal_port = internalPort;
      }
      if (formData.nvr_rtsp_port) {
        const rtspPort = parseInt(formData.nvr_rtsp_port);
        if (!isNaN(rtspPort)) payload.nvr_rtsp_port = rtspPort;
      }
      if (formData.nvr_internal_rtsp_port) {
        const internalRtspPort = parseInt(formData.nvr_internal_rtsp_port);
        if (!isNaN(internalRtspPort)) payload.nvr_internal_rtsp_port = internalRtspPort;
      }
      if (formData.nvr_username) payload.nvr_username = formData.nvr_username;
      if (formData.nvr_password_encrypted) payload.nvr_password_encrypted = formData.nvr_password_encrypted;
      if (formData.nvr_type) payload.nvr_type = formData.nvr_type;
      if (formData.nvr_model) payload.nvr_model = formData.nvr_model;
      if (formData.nvr_channel_count) {
        const channels = parseInt(formData.nvr_channel_count);
        if (!isNaN(channels)) payload.nvr_channel_count = channels;
      }

      // Transcoding Configuration
      payload.transcoding_enabled = formData.transcoding_enabled;
      if (formData.transcoding_enabled) {
        if (formData.transcoding_device_ip) payload.transcoding_device_ip = formData.transcoding_device_ip;
        if (formData.transcoding_device_port) {
          const port = parseInt(formData.transcoding_device_port);
          if (!isNaN(port)) payload.transcoding_device_port = port;
        }
        if (formData.transcoding_device_api_port) {
          const apiPort = parseInt(formData.transcoding_device_api_port);
          if (!isNaN(apiPort)) payload.transcoding_device_api_port = apiPort;
        }
        if (formData.transcoding_device_username) payload.transcoding_device_username = formData.transcoding_device_username;
        if (formData.transcoding_device_password_encrypted) payload.transcoding_device_password_encrypted = formData.transcoding_device_password_encrypted;
        if (formData.transcoding_device_status) payload.transcoding_device_status = formData.transcoding_device_status;
      }

      // External Access Configuration
      payload.external_access_enabled = formData.external_access_enabled;
      if (formData.external_access_enabled) {
        payload.external_http_port = formData.external_http_port ? Number(formData.external_http_port) : null;
        if (formData.external_hostname) payload.external_hostname = formData.external_hostname;
        if (formData.nvr_username) payload.nvr_username = formData.nvr_username;
        if (formData.nvr_password_encrypted) payload.nvr_password_encrypted = formData.nvr_password_encrypted;
        if (formData.external_transcoding_port) {
          const port = parseInt(formData.external_transcoding_port);
          if (!isNaN(port)) payload.external_transcoding_port = port;
        }
        if (formData.external_rtsp_port) {
          const port = parseInt(formData.external_rtsp_port);
          if (!isNaN(port)) payload.external_rtsp_port = port;
        }
      }

      // Stream Authentication Configuration
      payload.stream_auth_enabled = formData.stream_auth_enabled;
      if (formData.stream_auth_enabled) {
        if (formData.location_identifier) payload.location_identifier = formData.location_identifier;
        if (formData.stream_auth_port) {
          const port = parseInt(formData.stream_auth_port);
          if (!isNaN(port)) payload.stream_auth_port = port;
        }
        // Only send secret if it's not empty (user wants to change it)
        if (formData.stream_shared_secret) payload.stream_shared_secret = formData.stream_shared_secret;
      }

      // Settings
      payload.ycl_events_enabled = formData.ycl_events_enabled;
      payload.ycl_entry_logging = formData.ycl_entry_logging;
      
      // Auto-refresh settings
      payload.auto_refresh_enabled = formData.auto_refresh_enabled;
      payload.auto_refresh_interval_minutes = parseFloat(formData.auto_refresh_interval_minutes as any) || 9.5;
      payload.ycl_defaut_location = formData.ycl_defaut_location;
      payload.status = formData.status;
      payload.timezone_offset_hours = parseInt(formData.timezone_offset_hours.toString());
      payload.dst_enabled = formData.dst_enabled;
      payload.location_timezone = formData.location_timezone;

      await locationService.updateLocation(location.ycl_id, payload);
      navigate('/locations');
    } catch (err: any) {
      console.error('Update location error:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      const errorMessage = err.response?.data?.message || (err instanceof Error ? err.message : 'Failed to update location');
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" text="Loading location..." />
        </div>
      </MainLayout>
    );
  }

  if (!location) {
    return (
      <MainLayout>
        <Alert variant="error" message="Location not found" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/locations')}
            >
              Back to Locations
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">Edit Location</h1>
              <p className="text-text-muted">Update location information</p>
            </div>
          </div>
        </div>

        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Same form content as CreateLocationPage */}
          {/* Basic Information */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Location Name *"
                  placeholder="e.g., OBX Office"
                  value={formData.ycl_name}
                  onChange={(e) => setFormData({ ...formData, ycl_name: e.target.value })}
                  error={formErrors.ycl_name}
                  fullWidth
                />
                <Input
                  label="Location Code *"
                  placeholder="e.g., OBX001"
                  value={formData.ycl_code}
                  onChange={(e) => setFormData({ ...formData, ycl_code: e.target.value })}
                  error={formErrors.ycl_code}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Location Type"
                  placeholder="e.g., Warehouse, Office"
                  value={formData.ycl_type}
                  onChange={(e) => setFormData({ ...formData, ycl_type: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Company ID"
                  type="number"
                  placeholder="e.g., 1"
                  value={formData.ycl_company}
                  onChange={(e) => setFormData({ ...formData, ycl_company: e.target.value })}
                  fullWidth
                />
              </div>
              <Textarea
                label="Description"
                placeholder="Enter location description"
                value={formData.ycl_description}
                onChange={(e) => setFormData({ ...formData, ycl_description: e.target.value })}
                fullWidth
              />
            </div>
          </Card>

          {/* Address Information */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Address Information</h3>
            <div className="space-y-4">
              <Input
                label="Street Address"
                placeholder="e.g., 123 Main Street"
                value={formData.ycl_address}
                onChange={(e) => setFormData({ ...formData, ycl_address: e.target.value })}
                fullWidth
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City"
                  placeholder="e.g., Outer Banks"
                  value={formData.ycl_city}
                  onChange={(e) => setFormData({ ...formData, ycl_city: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Province/State"
                  placeholder="e.g., NC"
                  value={formData.ycl_province}
                  onChange={(e) => setFormData({ ...formData, ycl_province: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Postal Code"
                  placeholder="e.g., 27948"
                  value={formData.ycl_postal_code}
                  onChange={(e) => setFormData({ ...formData, ycl_postal_code: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Caller ID"
                  placeholder="e.g., +1-252-123-4567"
                  value={formData.ycl_caller_id}
                  onChange={(e) => setFormData({ ...formData, ycl_caller_id: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Latitude"
                  type="number"
                  step="any"
                  placeholder="e.g., 35.8"
                  value={formData.ycl_latitude}
                  onChange={(e) => setFormData({ ...formData, ycl_latitude: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Longitude"
                  type="number"
                  step="any"
                  placeholder="e.g., -75.5"
                  value={formData.ycl_longitude}
                  onChange={(e) => setFormData({ ...formData, ycl_longitude: e.target.value })}
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* Cloud Provider Credentials - Always show */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Cloud Provider Credentials</h3>
            <div className="space-y-4">
              <p className="text-text-muted text-sm">
                Optional: Configure cloud API credentials for HikVision, Dahua, or other cloud providers. Leave blank if using local NVR only.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="API Key / AppKey"
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  helperText="Cloud provider API key"
                  fullWidth
                />
                <Input
                  label="App Secret / SecretKey"
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={formData.app_secret}
                  onChange={(e) => setFormData({ ...formData, app_secret: e.target.value })}
                  helperText="Cloud provider secret key"
                  fullWidth
                />
                <Input
                  label="Verification Code"
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={formData.cloud_verification_code}
                  onChange={(e) => setFormData({ ...formData, cloud_verification_code: e.target.value })}
                  helperText="Stream verification code"
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* NVR Configuration - Always show */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">NVR Configuration</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR IP Address"
                  placeholder="e.g., 192.168.1.100"
                  value={formData.nvr_ip}
                  onChange={(e) => setFormData({ ...formData, nvr_ip: e.target.value })}
                  fullWidth
                />
                <Input
                  label="NVR Port (ISAPI)"
                  type="number"
                  placeholder="e.g., 80"
                  value={formData.nvr_port}
                  onChange={(e) => setFormData({ ...formData, nvr_port: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR Internal Port (LAN)"
                  type="number"
                  placeholder="e.g., 8075"
                  value={formData.nvr_internal_port}
                  onChange={(e) => setFormData({ ...formData, nvr_internal_port: e.target.value })}
                  helperText="HTTP/ISAPI port on local network"
                  fullWidth
                />
                <Input
                  label="NVR RTSP Port"
                  type="number"
                  placeholder="e.g., 554"
                  value={formData.nvr_rtsp_port}
                  onChange={(e) => setFormData({ ...formData, nvr_rtsp_port: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR Internal RTSP Port (LAN)"
                  type="number"
                  placeholder="e.g., 8081"
                  value={formData.nvr_internal_rtsp_port}
                  onChange={(e) => setFormData({ ...formData, nvr_internal_rtsp_port: e.target.value })}
                  helperText="RTSP port on local network"
                  fullWidth
                />
                <Input
                  label="NVR Username"
                  placeholder="e.g., admin"
                  value={formData.nvr_username}
                  onChange={(e) => setFormData({ ...formData, nvr_username: e.target.value })}
                  fullWidth
                />
                <Input
                  label="NVR Password"
                  type="password"
                  placeholder="Enter NVR password"
                  value={formData.nvr_password_encrypted}
                  onChange={(e) => setFormData({ ...formData, nvr_password_encrypted: e.target.value })}
                  fullWidth
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="NVR Type"
                  placeholder="e.g., Hikvision, Dahua"
                  value={formData.nvr_type}
                  onChange={(e) => setFormData({ ...formData, nvr_type: e.target.value })}
                  fullWidth
                />
                <Input
                  label="NVR Model"
                  placeholder="e.g., DS-7616NI-K2"
                  value={formData.nvr_model}
                  onChange={(e) => setFormData({ ...formData, nvr_model: e.target.value })}
                  fullWidth
                />
              </div>
              <Input
                label="NVR Channel Count"
                type="number"
                placeholder="e.g., 16"
                value={formData.nvr_channel_count}
                onChange={(e) => setFormData({ ...formData, nvr_channel_count: e.target.value })}
                fullWidth
              />
            </div>
          </Card>

          {/* Transcoding Configuration */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Transcoding Configuration</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.transcoding_enabled}
                  onChange={(e) => setFormData({ ...formData, transcoding_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Transcoding</span>
              </label>
              {formData.transcoding_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Transcoding Device IP"
                      placeholder="e.g., 192.168.1.200"
                      value={formData.transcoding_device_ip}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_ip: e.target.value })}
                      fullWidth
                    />
                    <Input
                      label="Device Port"
                      type="number"
                      placeholder="e.g., 8080"
                      value={formData.transcoding_device_port}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_port: e.target.value })}
                      fullWidth
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="API Port"
                      type="number"
                      placeholder="e.g., 8000"
                      value={formData.transcoding_device_api_port}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_api_port: e.target.value })}
                      fullWidth
                    />
                    <Input
                      label="Username"
                      placeholder="e.g., admin"
                      value={formData.transcoding_device_username}
                      onChange={(e) => setFormData({ ...formData, transcoding_device_username: e.target.value })}
                      fullWidth
                    />
                  </div>
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Enter transcoding device password"
                    value={formData.transcoding_device_password_encrypted}
                    onChange={(e) => setFormData({ ...formData, transcoding_device_password_encrypted: e.target.value })}
                    fullWidth
                  />
                  <Select
                    label="Device Status"
                    value={formData.transcoding_device_status}
                    onChange={(e) => setFormData({ ...formData, transcoding_device_status: e.target.value })}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Inactive', value: 'inactive' },
                    ]}
                    fullWidth
                  />
                </>
              )}
            </div>
          </Card>

          {/* External Access Configuration */}
          <Card padding="lg" className="border-l-4 border-l-green-500">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              External NVR Access {isCloudLocation && '(Optional)'}
            </h3>
            <div className="space-y-4">
              {isCloudLocation && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-200">
                    <strong>Cloud + NVR Integration:</strong> This location uses cloud provider integration for camera management. 
                    If your cloud-synced cameras also have a local NVR accessible externally, enable this to fetch intelligent metadata 
                    (behavior analysis, face detection, intrusion zones) and accurate playback timestamps via NVR ISAPI endpoints.
                  </p>
                </div>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.external_access_enabled}
                  onChange={(e) => setFormData({ ...formData, external_access_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable External NVR Access</span>
              </label>
              {formData.external_access_enabled && (
                <>
                  <Input
                    label="External Hostname"
                    placeholder="e.g., office.ddns.net or office.ddns.net:8093"
                    value={formData.external_hostname}
                    onChange={(e) => setFormData({ ...formData, external_hostname: e.target.value })}
                    fullWidth
                    helperText="DDNS hostname, public IP or HTTP/HTTPS address. An inline port takes priority."
                    error={formErrors.external_hostname}
                  />
                  <Input label="NVR HTTP/ISAPI Port" type="number" min={1} max={65535} step={1}
                    placeholder="e.g., 8093" value={formData.external_http_port}
                    onChange={(e) => setFormData({ ...formData, external_http_port: e.target.value })} fullWidth
                    helperText="Port forwarded to the NVR HTTP/ISAPI service for event recordings. Leave blank to use the hostname port or saved NVR HTTP port." />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="NVR Username"
                      placeholder="e.g., admin"
                      value={formData.nvr_username}
                      onChange={(e) => setFormData({ ...formData, nvr_username: e.target.value })}
                      fullWidth
                      helperText={isCloudLocation ? 'Username for NVR ISAPI access' : 'Username for remote NVR'}
                    />
                    <Input
                      label="NVR Password"
                      type="password"
                      placeholder="Enter NVR password"
                      value={formData.nvr_password_encrypted}
                      onChange={(e) => setFormData({ ...formData, nvr_password_encrypted: e.target.value })}
                      fullWidth
                      helperText={isCloudLocation ? 'Password for NVR ISAPI access' : 'Password for remote NVR'}
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card padding="lg">
            <details>
              <summary className="cursor-pointer text-sm font-semibold text-text-primary">Other streaming services (optional)</summary>
              <p className="my-3 text-sm text-text-secondary">These ports belong to streaming and transcoding services. Event recording downloads use the NVR HTTP/ISAPI port above.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="External Transcoding Port"
                      type="number"
                      placeholder="e.g., 1984"
                      value={formData.external_transcoding_port}
                      onChange={(e) => setFormData({ ...formData, external_transcoding_port: e.target.value })}
                      fullWidth
                    />
                    <Input
                      label="External RTSP Port"
                      type="number"
                      placeholder="e.g., 8554"
                      value={formData.external_rtsp_port}
                      onChange={(e) => setFormData({ ...formData, external_rtsp_port: e.target.value })}
                      fullWidth
                    />
                  </div>
            </details>
          </Card>

          {/* Stream Authentication Configuration */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Stream Authentication (JWT)</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.stream_auth_enabled}
                  onChange={(e) => setFormData({ ...formData, stream_auth_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable JWT Stream Authentication</span>
              </label>
              <p className="text-sm text-text-secondary">
                Requires camera streams to use JWT tokens. Enable this for secure remote access.
              </p>
              {formData.stream_auth_enabled && (
                <>
                  <Input
                    label="Location Identifier"
                    placeholder="e.g., secureview-n95-001"
                    value={formData.location_identifier}
                    onChange={(e) => setFormData({ ...formData, location_identifier: e.target.value })}
                    fullWidth
                    helperText="Must match the LOCATION_ID on your edge device"
                  />
                  <Input
                    label="Stream Auth Port"
                    type="number"
                    placeholder="8000"
                    value={formData.stream_auth_port}
                    onChange={(e) => setFormData({ ...formData, stream_auth_port: e.target.value })}
                    fullWidth
                    helperText="Port where the JWT auth gateway is running (default: 8000)"
                  />
                  <Input
                    label="Shared Secret Key"
                    type="password"
                    placeholder="Enter secret key"
                    value={formData.stream_shared_secret}
                    onChange={(e) => setFormData({ ...formData, stream_shared_secret: e.target.value })}
                    fullWidth
                    helperText="Leave blank to keep current secret. Must match SHARED_SECRET on edge device."
                  />
                </>
              )}
            </div>
          </Card>

          {/* Settings */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Settings</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_events_enabled}
                  onChange={(e) => setFormData({ ...formData, ycl_events_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Events</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_entry_logging}
                  onChange={(e) => setFormData({ ...formData, ycl_entry_logging: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Entry Logging</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Active Status</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ycl_defaut_location}
                  onChange={(e) => setFormData({ ...formData, ycl_defaut_location: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Default Location for Company</span>
              </label>
            </div>
          </Card>

          {/* Live View Auto-Refresh Settings */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Live View Grid Auto-Refresh</h3>
            <p className="text-sm text-text-secondary mb-4">
              Configure automatic refresh of camera streams in fullscreen grid view to renew authentication tokens and prevent stream expiration.
            </p>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.auto_refresh_enabled}
                  onChange={(e) => setFormData({ ...formData, auto_refresh_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-border bg-surface-secondary text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-text-primary">Enable Auto-Refresh</span>
              </label>
              
              {formData.auto_refresh_enabled && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Refresh Interval (minutes)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    step="0.5"
                    value={formData.auto_refresh_interval_minutes}
                    onChange={(e) => setFormData({ ...formData, auto_refresh_interval_minutes: parseFloat(e.target.value) || 9.5 })}
                    placeholder="9.5"
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Recommended: 9.5 minutes (9 min 30 sec). Range: 1-60 minutes.
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Timezone Configuration */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Location Timezone</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  NVR Timezone
                </label>
                <Select
                  value={formData.location_timezone || 'America/Toronto'}
                  onChange={(e) => setFormData({ ...formData, location_timezone: e.target.value })}
                  options={[
                    { label: 'Eastern Time (Toronto, New York, Miami)', value: 'America/Toronto' },
                    { label: 'Central Time (Chicago, Dallas, Mexico City)', value: 'America/Chicago' },
                    { label: 'Mountain Time (Denver, Phoenix)', value: 'America/Denver' },
                    { label: 'Pacific Time (Los Angeles, Vancouver)', value: 'America/Los_Angeles' },
                    { label: 'Alaska Time (Anchorage)', value: 'America/Anchorage' },
                    { label: 'Hawaii Time (Honolulu)', value: 'Pacific/Honolulu' },
                    { label: 'Atlantic Time (Halifax)', value: 'America/Halifax' },
                    { label: 'UTC (Universal Time)', value: 'UTC' },
                    { label: 'London (GMT/BST)', value: 'Europe/London' },
                    { label: 'Paris (CET/CEST)', value: 'Europe/Paris' },
                    { label: 'Dubai (GST)', value: 'Asia/Dubai' },
                    { label: 'India (IST)', value: 'Asia/Kolkata' },
                    { label: 'China (CST)', value: 'Asia/Shanghai' },
                    { label: 'Singapore (SGT)', value: 'Asia/Singapore' },
                    { label: 'Japan (JST)', value: 'Asia/Tokyo' },
                    { label: 'Australia Sydney (AEDT/AEST)', value: 'Australia/Sydney' },
                  ]}
                />
                <p className="text-xs text-text-secondary mt-1">
                  Select the timezone where your NVR is located. Events will be stored in this timezone for accurate playback and metadata retrieval. Automatically handles daylight saving time.
                </p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-surface-primary p-4 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/locations')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<Save size={18} />}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditLocationPage;
